/**
 * Starfield Component
 * Renders an animated starfield background with twinkling particles
 *
 * Extracted from App.tsx for better organization and testability
 */

import React from 'react';
import type { Particle } from '../types';

// ============================================================================
// Props Interface
// ============================================================================

export interface StarfieldProps {
  /** Array of particle objects to render */
  particles: Particle[];
}

// ============================================================================
// Component
// ============================================================================

/**
 * Starfield - Animated background with twinkling star particles
 *
 * Features:
 * - Positioned absolutely to fill parent container
 * - Non-interactive (pointer-events-none)
 * - CSS-based twinkle animation
 * - Memoized to prevent unnecessary re-renders
 */
export const Starfield: React.FC<StarfieldProps> = React.memo(({ particles }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {particles.map((particle) => (
      <div
        key={particle.id}
        className="absolute rounded-full bg-white animate-twinkle"
        style={{
          left: `${particle.x}%`,
          top: `${particle.y}%`,
          width: `${particle.size}px`,
          height: `${particle.size}px`,
          opacity: particle.opacity ?? 0.6,
          '--twinkle-duration': `${particle.duration}s`,
          '--twinkle-delay': `${particle.delay}s`
        } as React.CSSProperties}
      />
    ))}
  </div>
));

Starfield.displayName = 'Starfield';
